/**
 * Public Key Modal
 * Modal for picking (or pasting) the SSH public key used to sign a
 * 24-hour edge-access certificate.
 *
 * VSNP-680 / re-arch C: prefer saved profile keys over pasting. The
 * modal fetches the user's saved keys on open, defaults to picker mode
 * if any exist, and falls back to a paste form via "Add new key".
 */

import React, { useEffect, useState } from 'react';
import { X, Key, Info, Plus } from 'lucide-react';
import userSshKeysService, { type UserSshKey } from '../../services/userSshKeys.service';

export interface PublicKeySelection {
  publicKey?: string;        // paste flow
  userKeyId?: string;        // saved-key flow
  // Paste-flow only. Backend will auto-save the pasted key to the user's
  // profile under this label so it's picker-selectable next time.
  keyLabel?: string;
  // Where the matching private key lives on the user's filesystem. Used
  // to render the IdentityFile line in the generated ~/.ssh/config. The
  // backend falls back to a per-algo default (~/.ssh/id_ed25519, etc.)
  // when omitted.
  privateKeyPath?: string;
}

/**
 * Mirror of backend `defaultPrivateKeyPath` so the modal can pre-fill the
 * "private key file" field as the user pastes. Keep in sync with
 * vsm-api-v2/src/services/userSshKeysService.ts.
 */
function inferPrivateKeyPath(publicKey: string): string {
  const algo = publicKey.trim().split(/\s+/)[0] || '';
  if (algo === 'ssh-ed25519') return '~/.ssh/id_ed25519';
  if (algo === 'ssh-rsa') return '~/.ssh/id_rsa';
  if (algo.startsWith('ecdsa-sha2-')) return '~/.ssh/id_ecdsa';
  if (algo === 'ssh-dss') return '~/.ssh/id_dsa';
  return '~/.ssh/id_ed25519';
}

interface PublicKeyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selection: PublicKeySelection) => void;
  isLoading?: boolean;
}

export const PublicKeyModal: React.FC<PublicKeyModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [savedKeys, setSavedKeys] = useState<UserSshKey[]>([]);
  const [savedKeysLoading, setSavedKeysLoading] = useState(false);
  const [pickedKeyId, setPickedKeyId] = useState<string>('');
  const [pasteMode, setPasteMode] = useState(false);
  const [publicKey, setPublicKey] = useState('');
  const [pasteLabel, setPasteLabel] = useState('');
  const [privateKeyPath, setPrivateKeyPath] = useState('');
  const [pathEdited, setPathEdited] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        setSavedKeysLoading(true);
        const keys = await userSshKeysService.list();
        if (cancelled) return;
        setSavedKeys(keys);
        // Default to picker mode if any saved keys; auto-select the most
        // recently used (or first).
        if (keys.length > 0) {
          const mostRecent = [...keys].sort((a, b) => {
            const ta = a.lastUsedAt || a.createdAt || '';
            const tb = b.lastUsedAt || b.createdAt || '';
            return ta < tb ? 1 : -1;
          })[0];
          setPickedKeyId(mostRecent.id);
          setPasteMode(false);
        } else {
          setPasteMode(true);
        }
      } catch {
        // Fall back to paste mode on any failure
        setPasteMode(true);
      } finally {
        if (!cancelled) setSavedKeysLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (pasteMode) {
      const trimmed = publicKey.trim();
      if (!trimmed) {
        setError('Public key is required');
        return;
      }
      if (!trimmed.match(/^(ssh-ed25519|ssh-rsa|ecdsa-sha2-nistp256|ecdsa-sha2-nistp384|ecdsa-sha2-nistp521)\s+[A-Za-z0-9+/=]+/)) {
        setError('Invalid public key format. Should start with ssh-ed25519, ssh-rsa, ecdsa-sha2-*, etc.');
        return;
      }
      const trimmedPath = privateKeyPath.trim();
      if (trimmedPath && /["'\n\r]/.test(trimmedPath)) {
        setError('Private key file path contains invalid characters');
        return;
      }
      onSubmit({
        publicKey: trimmed,
        keyLabel: pasteLabel.trim() || undefined,
        // Send the path if the user customized it OR keep blank to let the
        // backend fill in the per-algo default. We always send the effective
        // value (inferred default if untouched) so the install script uses
        // the right IdentityFile line straight away.
        privateKeyPath: trimmedPath || inferPrivateKeyPath(trimmed),
      });
      return;
    }

    if (!pickedKeyId) {
      setError('Pick a saved key or switch to paste mode');
      return;
    }
    onSubmit({ userKeyId: pickedKeyId });
  };

  const handleClose = () => {
    if (isLoading) return;
    setPublicKey('');
    setPasteLabel('');
    setPrivateKeyPath('');
    setPathEdited(false);
    setError('');
    setPickedKeyId('');
    setPasteMode(false);
    onClose();
  };

  // Keep the IdentityFile field in sync with the pasted key's algorithm UNLESS
  // the user has manually edited it (then leave their value alone).
  const onPasteChange = (next: string) => {
    setPublicKey(next);
    setError('');
    if (!pathEdited) {
      setPrivateKeyPath(inferPrivateKeyPath(next));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Key className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Generate SSH User Certificate
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {savedKeysLoading ? (
              <div className="text-sm text-gray-500">Loading your saved SSH keys…</div>
            ) : !pasteMode && savedKeys.length > 0 ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Choose a saved SSH key</label>
                  <select
                    value={pickedKeyId}
                    onChange={(e) => setPickedKeyId(e.target.value)}
                    disabled={isLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    {savedKeys.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.label} — {k.fingerprint} · {k.privateKeyPath}{k.autoRenew ? ' · auto-renew' : ''}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Manage saved keys in <a href="/profile" className="text-blue-600 hover:underline">My Profile → SSH Keys</a>.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setPasteMode(true)}
                  className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" /> Paste a different key (one-off — not saved to profile)
                </button>
              </>
            ) : (
              <>
                {savedKeys.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPasteMode(false)}
                    className="text-sm text-blue-600 hover:underline"
                  >
                    ← Back to saved keys
                  </button>
                )}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-900">
                      <p className="font-semibold mb-2">How to find your SSH public key:</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>Open your terminal</li>
                        <li>List existing keys: <code className="bg-blue-100 px-2 py-0.5 rounded">ls ~/.ssh/*.pub</code></li>
                        <li>Print one (typically <code className="bg-blue-100 px-1 py-0.5 rounded">id_ed25519.pub</code>, <code className="bg-blue-100 px-1 py-0.5 rounded">id_rsa.pub</code>, or <code className="bg-blue-100 px-1 py-0.5 rounded">id_ecdsa.pub</code>): <code className="bg-blue-100 px-2 py-0.5 rounded">cat ~/.ssh/&lt;name&gt;.pub</code></li>
                        <li>No keys? Generate one: <code className="bg-blue-100 px-2 py-0.5 rounded">ssh-keygen -t ed25519</code></li>
                        <li>Paste the public key below — the matching private key stays on your machine.</li>
                      </ol>
                      <p className="mt-2 text-xs">
                        ed25519, RSA, and ECDSA keys are all accepted. We'll save this key to your profile so you can pick it from the list next time.
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="publicKey" className="block text-sm font-medium text-gray-700 mb-2">
                    SSH Public Key *
                  </label>
                  <textarea
                    id="publicKey"
                    value={publicKey}
                    onChange={(e) => onPasteChange(e.target.value)}
                    placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAI... user@host"
                    className={`w-full h-32 px-3 py-2 border ${error ? 'border-red-300' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm`}
                    disabled={isLoading}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="pasteLabel" className="block text-sm font-medium text-gray-700 mb-2">
                      Label
                    </label>
                    <input
                      id="pasteLabel"
                      type="text"
                      value={pasteLabel}
                      onChange={(e) => setPasteLabel(e.target.value)}
                      placeholder={`Auto-saved ${new Date().toISOString().slice(0, 10)}`}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                      disabled={isLoading}
                    />
                    <p className="text-xs text-gray-500 mt-1">Shown when picking this key later.</p>
                  </div>
                  <div>
                    <label htmlFor="privateKeyPath" className="block text-sm font-medium text-gray-700 mb-2">
                      Private key file (on your machine)
                    </label>
                    <input
                      id="privateKeyPath"
                      type="text"
                      value={privateKeyPath}
                      onChange={(e) => { setPrivateKeyPath(e.target.value); setPathEdited(true); }}
                      placeholder="~/.ssh/id_ed25519"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                      disabled={isLoading}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Used in the generated <code>~/.ssh/config</code> as <code>IdentityFile</code>. Auto-filled from the public key's algorithm — change it only if your private key has a custom name.
                    </p>
                  </div>
                </div>
              </>
            )}

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> The certificate is valid for <strong>24 hours</strong> and signs in as <code className="bg-gray-200 px-1 rounded">edge</code> (or <code className="bg-gray-200 px-1 rounded">root</code> on legacy edges).
              </p>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || (pasteMode ? !publicKey.trim() : !pickedKeyId)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Generating...
                </>
              ) : (
                <>
                  <Key className="h-4 w-4" />
                  Generate Certificate
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PublicKeyModal;
